import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import localStyles from './info.module.scss';
import { Icon } from '@penta/penta-shared';

export const Route = createFileRoute('/$locale/smartbanking/info')({
  component: Page,
});

function Page() {
  const { t } = useTranslation();
  return (
    <>
      <header>
        <h1>{t('sidebar.info')}</h1>
      </header>

      <div className={localStyles.container}>
        <div className={localStyles.warningBox}>
          <h2>
            <Icon name="warning" size={24} aria-label="Warning" /> Rychle
            "naflákané" demo pro prezentační účely
          </h2>
          <p>
            Tato aplikace slouží pouze jako <strong>vizuální</strong> ukázka.
            Pro finální produkční verzi je nutné vyřešit řadu technických a
            procesních kroků.
          </p>
          <p className={localStyles.invisibleWorkNote}>
            Většina níže zmíněných bodů představuje &quot;neviditelnou&quot;
            práci na pozadí. Vizuální stránka aplikace je pouze špička ledovce,
            zatímco většina úsilí spadá do architektury, bezpečnosti, stability
            a procesů, které uživatel na první pohled nevidí, ale jsou pro
            bankovní aplikaci kritické.
          </p>
        </div>

        <h3>Roadmapa pro finální aplikaci:</h3>
        <ul className={localStyles.roadmapList}>
          <li>
            <strong>1. Best Practices a Guidelines:</strong> Nastavení a
            vynucování pravidel pro psaní kódu (linting, formatting),
            konzistence názvosloví, code review procesy.
          </li>
          <li>
            <strong>2. Struktura repozitáře:</strong> Pořádná, promyšlená
            struktura celého repozitáře (Nx workspace standards).
          </li>
          <li>
            <strong>3. CI (Continuous Integration) pro DEV:</strong> Pipeline
            pro automatizované buildy, linting, unit a e2e testy.
          </li>
          <li>
            <strong>4. Local Development:</strong> Komplexní MSW mocky
            odpovídající reálnému API pro offline/izolovaný vývoj.
          </li>
          <li>
            <strong>5. Design System:</strong> Připravené základní styly a
            design tokeny (CSS variables) v systematické formě pro konzistenci
            vývoje.
          </li>
          <li>
            <strong>6. Responsivní Layout:</strong> Robustní layout řešící
            plynulé zobrazení pro <strong>mobilní telefony</strong>,{' '}
            <strong>tablety</strong>, <strong>desktopy</strong> i{' '}
            <strong>XXL monitory</strong> (ultrawide). Včetně přístupnosti (skip
            links, landmarks).
          </li>
          <li>
            <strong>7. Sdílená knihovna:</strong> Vytvoření sdílené knihovny
            znovupoužitelných komponent. To je klíčové pro{' '}
            <strong>systematičnost</strong> (jedna pravda),{' '}
            <strong>konzistenci</strong> (vizuální i funkční shoda napříč
            aplikací) a <strong>udržitelnost</strong> (oprava chyby v tlačítku
            se promítne všude).
          </li>
          <li>
            <strong>8. Komponenty:</strong> Vytvoření všech dílčích komponent
            dle standardů:
            <ul>
              <li>
                Každá komponenta musí obsahovat: a11y, unit testy, styling pro
                obě varianty theme, funkční a businessovou logiku.
              </li>
            </ul>
          </li>
          <li>
            <strong>9. API Integrace:</strong> Generování klienta z OpenAPI
            schématu, type-safe fetching, OAuth integrace.
          </li>
          <li>
            <strong>10. Security:</strong> Auth guardy pro ochranu cest, řešení
            token refresh flow, zabezpečení hlaviček.
          </li>
          <li>
            <strong>11. i18n (Lokalizace):</strong> Systém textací pro napojení
            na CMS a podporu více jazyků.
          </li>
          <li>
            <strong>12. Error Handling:</strong> Globální Error Boundaries,
            toast notifikace pro chyby, graceful degradation.
          </li>
          <li>
            <strong>13. Testing Strategy:</strong> E2E testy (Playwright) a
            visual regression testy pro Chromium, WebKit a Firefox.
          </li>
          <li>
            <strong>14. Architektura a Výkon:</strong>
            <ul>
              <li>Lazy-loading jednotlivých stránek (code splitting).</li>
              <li>Efektivní lokální caching a invalidace (TanStack Query).</li>
              <li>Celková optimalizace bundle size.</li>
            </ul>
          </li>
          <li>
            <strong>15. CD (Continuous Delivery) pro DEV:</strong> Pipeline pro
            vytváření docker images, push do artifact registry a skripty pro
            automatický deploy.
          </li>
          <li>
            <strong>16. Monitoring:</strong> Nasazení logování a analytiky pro
            sledování chyb a výkonu v reálném čase.
          </li>
          <li>
            <strong>17. Dokumentace:</strong> Technická dokumentace
            architektury, README pro jednotlivé knihovny, generování dokumentace
            z kódu (JSDoc/TSDoc).
          </li>
          <li>
            <strong>18. Vulnerability Management:</strong> Automatizované
            skenování kódu (SonarQube) a závislostí (Dependabot) pro včasnou
            detekci bezpečnostních trhlin a outdated balíčků.
          </li>
        </ul>

        <h3>Specifika bankovního prostředí:</h3>
        <ul className={localStyles.roadmapList}>
          <li>
            <strong>SCA (Strong Customer Authentication):</strong> Nutnost
            implementace dvoufaktorové autentizace pro přihlášení a aktivní
            operace (platby) dle PSD2 regulací. Integrace s mobilním klíčem nebo
            SMS.
          </li>
          <li>
            <strong>Bezpečnost session:</strong> Automatické odhlášení při
            neaktivitě, detekce souběžných připojení, ochrana proti session
            hijacking.
          </li>
          <li>
            <strong>Ochrana citlivých dat:</strong> Maskování čísel karet a účtů
            v logách i na UI (např. odesílání výpisů), zákaz ukládání citlivých
            údajů do lokálního úložiště prohlížeče.
          </li>
          <li>
            <strong>Práce s penězi (Decimal Math):</strong> Zákaz používání
            standardního `float` pro finanční výpočty (problém 0.1 + 0.2).
            Nutnost použití knihoven pro přesnou aritmetiku (např. `decimal.js`
            nebo práce s haléři jako celými čísly).
          </li>
          <li>
            <strong>CSP (Content Security Policy):</strong> Striktní nastavení
            hlaviček bránící spuštění neautorizovaných skriptů (XSS ochrana).
          </li>
          <li>
            <strong>Auditní stopa:</strong> Každá akce uživatele musí být
            dohledatelná pro případné reklamace nebo bezpečnostní incidenty.
          </li>
          <li>
            <strong>Pokročilá validace formulářů:</strong> Specifická bankovní
            pravidla pro formuláře (např. kontrola Modulo 11 pro čísla účtů a
            variabilní symboly, validace IBAN, specifika pro zahraniční platby).
          </li>
          <li>
            <strong>Idempotence operací:</strong> Zajištění, že se platba
            neodešle dvakrát při výpadku sítě nebo dvojitém kliknutí (použití
            idempotency keys v API hlavičkách).
          </li>
          <li>
            <strong>Cut-off times a kalendář:</strong> Správné zobrazování data
            splatnosti s ohledem na bankovní svátky, víkendy a časy uzávěrek pro
            různé typy plateb (okamžité vs. standardní).
          </li>
          <li>
            <strong>Anti-fraud mechanismy:</strong> Pasivní sběr dat o zařízení
            (device fingerprinting), geolokace a behaviorální analýza pro
            detekci podezřelých aktivit.
          </li>
        </ul>

        <div className={localStyles.phasesBox}>
          <p>
            <strong>Poznámka:</strong> Výše uvedený seznam pokrývá převážně{' '}
            <strong>VÝVOJOVOU</strong> fázi. Životní cyklus aplikace však
            pokračuje dalšími klíčovými etapami:
          </p>
          <ul>
            <li>
              <strong>NASAZOVACÍ fáze:</strong> Automatizované nasazování na
              vyšší prostředí (INT, AKC, PROD) pro potřeby validace.
            </li>
            <li>
              <strong>TESTOVÁNÍ UI:</strong> Ověření vizuální stránky,
              responzivity a přístupnosti (a11y) před plnou integrací.
            </li>
            <li>
              <strong>INTEGRAČNÍ fáze:</strong> Napojení na reálný Core Banking
              System, Payment Gateways, kartová centra.
            </li>
            <li>
              <strong>POKROČILÉ TESTOVÁNÍ:</strong> Penetration testing
              (bezpečnost), UAT (User Acceptance Testing), Performance testy nad
              integrovaným řešením.
            </li>
            <li>
              <strong>PROVOZNÍ fáze:</strong> L1/L2/L3 support, Incident
              Management, patchování, správa certifikátů.
            </li>
          </ul>
          <p className={localStyles.helpNote}>
            Jsme schopni pomoci s infrastrukturou, kontejnerizací a nasazením do
            prostředí <strong>OpenShift / Kubernetes</strong>.
          </p>
        </div>

        <h3>Vysvětlení pojmů:</h3>
        <dl className={localStyles.definitionList}>
          <dt>a11y (Accessibility)</dt>
          <dd>
            Zkratka pro přístupnost (11 písmen mezi &#39;a&#39; a &#39;y&#39;).
            Znamená zpřístupnění webu pro lidi s různými handicapy (zrakové,
            motorické, kognitivní). Zahrnuje správné používání barev (kontrast),
            podporu čteček obrazovky (ARIA atributy) a ovládání pouze
            klávesnicí.
          </dd>

          <dt>i18n (Internationalization)</dt>
          <dd>
            Zkratka pro internationalization (18 písmen mezi &#39;i&#39; a
            &#39;n&#39;). Znamená přípravu aplikace na podporu více jazyků a
            regionálních nastavení (formáty dat, měn). Umožňuje snadné přidání
            nového jazyka bez zásahu do kódu.
          </dd>

          <dt>CMS (Content Management System)</dt>
          <dd>
            Systém pro správu obsahu. Umožňuje, aby texty, marketingové bannery
            a právní dokumenty v aplikaci spravovali editoři/copywriteři,
            nikoliv vývojáři. Změna textu tak nevyžaduje nový release aplikace.
            Termíny z CMS se často stahují dynamicky.
          </dd>

          <dt>Design Tokeny</dt>
          <dd>
            Nejmenší stavební prvky designu uložené jako proměnné (např. barvy,
            typografie, mezery). Místo &quot;hex kódu #007bff&quot; se v kódu
            používá &quot;var(--color-primary)&quot;. To zajišťuje 100% vizuální
            konzistenci napříč celou aplikací a snadné změny témat (např. Dark
            Mode).
          </dd>

          <dt>Unit Testy</dt>
          <dd>
            Testy zaměřené na nejmenší testovatelné části aplikace (funkce,
            komponenty) izolovaně od zbytku systému. Ověřují, zda daná funkce
            vrací pro konkrétní vstup očekávaný výstup. Běží velmi rychle a jsou
            základem kvality kódu.
          </dd>

          <dt>E2E (End-to-End) Testy</dt>
          <dd>
            Testy simulující reálného uživatele procházejícího aplikací v
            prohlížeči. Klikají na tlačítka, vyplňují formuláře a kontrolují,
            zda se aplikace chová správně jako celek (včetně backendu a databáze
            nebo mocků). Playwright je moderní nástroj pro tyto účely.
          </dd>

          <dt>CI/CD</dt>
          <dd>
            Automatizovaný proces vývoje.{' '}
            <strong>CI (Continuous Integration)</strong> zajišťuje, že každá
            změna v kódu je automaticky otestována.{' '}
            <strong>CD (Continuous Delivery/Deployment)</strong> zajišťuje, že
            otestovaná verze je automaticky nebo na jedno kliknutí nasazena do
            produkce.
          </dd>

          <dt>Monorepo</dt>
          <dd>
            Nástroj pro správu více projektů (aplikací a knihoven) v jednom
            repozitáři. Umožňuje sdílení kódu, efektivní buildy (pouze změněné
            části) a vynucuje architektonická pravidla (např. kdo může na čem
            záviset).
          </dd>

          <dt>Kontejnerizace (Docker, Kubernetes/OpenShift)</dt>
          <dd>
            Technologie umožňující zabalit aplikaci se všemi jejími závislostmi
            do izolovaného &quot;kontejneru&quot;. To zaručuje, že aplikace
            poběží stejně na počítači vývojáře, na testovacím prostředí i na
            produkci. OpenShift a Kubernetes jsou nástroje pro hromadnou správu,
            škálování a provoz těchto kontejnerů (tzv. orchestrace).
          </dd>

          <dt>Prostředí (DEV, INT, AKC/UAT, PROD)</dt>
          <dd>
            Standardní členění infrastruktury. <strong>DEV (Vývojové)</strong>{' '}
            slouží vývojářům pro průběžnou integraci změn.{' '}
            <strong>INT (Integrační)</strong> slouží pro testování propojení
            systémů. <strong>AKC (Akceptační)</strong> nebo též{' '}
            <strong>UAT (User Acceptance Testing)</strong> slouží pro finální
            ověření funkčnosti klientem/testery před nasazením.{' '}
            <strong>PROD (Produkční)</strong> je živé prostředí pro reálné
            uživatele.
          </dd>

          <dt>Penetration Testing (Pen Testy)</dt>
          <dd>
            Simulované kybernetické útoky na vlastní systém s cílem odhalit
            bezpečnostní zranitelnosti (chyby v kódu, špatná konfigurace, úniky
            dat) dříve, než je objeví skuteční útočníci.
          </dd>

          <dt>L1 / L2 / L3 Support</dt>
          <dd>
            Úrovně technické podpory. <strong>L1</strong> je první linie
            kontaktu (základní problémy). <strong>L2</strong> řeší složitější
            technické problémy a konfigurace. <strong>L3</strong> jsou experti a
            přímo vývojáři, kteří řeší chyby přímo v kódu aplikace (hotfixy).
          </dd>

          <dt>SonarQube (Statická analýza)</dt>
          <dd>
            Nástroj, který automaticky čte kód a hledá v něm chyby, bezpečnostní
            díry a &quot;smelly code&quot; (špatně napsaný kód), aniž by se
            aplikace musela spustit. Slouží jako automatická kontrola kvality.
          </dd>

          <dt>Dependabot (Skenování závislostí)</dt>
          <dd>
            Automatický robot, který hlídá verze použitých knihoven (např.
            React). Pokud vyjde nová verze nebo je nalezena bezpečnostní chyba v
            aktuální verzi, robot sám vytvoří návrh na aktualizaci (Pull
            Request).
          </dd>

          <dt>Analytika a Marketing (GTM, GA4, Adobe)</dt>
          <dd>
            Nástroje pro měření chování uživatelů (klikání, průchody procesem).
            V bankovnictví podléhají nejpřísnějším regulacím. Nástroje{' '}
            <strong>nesmí</strong> mít přístup k citlivým datům (zůstatky, čísla
            účtů). Často se používá tzv. <strong>Data Layer</strong>, která
            posílá pouze anonymizované signály (např. &quot;uživatel dokončil
            platbu&quot;), nikoliv detaily transakce.
          </dd>
        </dl>
      </div>
    </>
  );
}
